import React, { useState } from 'react'
import classnames from 'classnames'
import find from 'lodash/find'
import filter from 'lodash/filter'
import ActionDropdown from '@SiteComponents/ActionDropdown'
import getProjectStatusForProjectsList from '@Utils/GetProjectStatusForProjectsList'
import getAllowLinkToEditor from '@Utils/GetAllowLinkToEditor'
import getShouldAllowToAddLanguages from '@Utils/GetShouldAllowToAddLanguages'
import getFormattedDuration from '@Utils/GetFormattedDuration'
import getLanguageStatusForLanguagesView from '@Utils/GetLanguageStatusForLanguagesView'
import getProjectVersionDisplayKind from '@Utils/GetProjectVersionDisplayKind'
import getCleanDisplayTitle from '@Utils/GetCleanDisplayTitle'
import useTooltipDelay from '@Utils/UseTooltipDelay'
import TitleTooltip from '@SiteComponents/TitleTooltip'
import More from '@Images/projects_table_list_more.svg'
import Done from '@Images/languages_view_done.svg'
import InProgress from '@Images/languages_view_in_progress.svg'
import Pending from '@Images/languages_view_pending.svg'
import Video from '@Images/video.svg'
import Magic from '@Images/magic_generate.svg'

import {
  IProjectVersion,
} from '@SiteModules/ProjectVersion/types'

import {
  ISubtitle,
} from '@SiteModules/Subtitle/types'

import {
  IOpenDeleteProjectVersionPopupPayload,
  IOpenRenameProjectVersionPopupPayload,
} from '@SiteModules/Popup/types'

import './ProjectVersionRow.scss'

const getTranscript = (projectVersion: IProjectVersion): ISubtitle | undefined =>
  find(projectVersion.subtitles, subtitle => subtitle.attributes.transcription) as ISubtitle | undefined

const getDoneLanguagesCount = (projectVersion: IProjectVersion): number =>
  filter(projectVersion.subtitles, subtitle => getLanguageStatusForLanguagesView(subtitle.attributes.stage)[0] === 'done').length

interface ProjectVersionRowProps {
  readonly isOriginal: boolean
  readonly index: number
  readonly projectVersion: IProjectVersion
  readonly sourceThumbnailUrl?: string
  resetOpenedProject(): void
  setOpenedProjectVersionId(openedProjectVersionId: string): void
  handleOpenRenameProjectVersionPopup(payload: IOpenRenameProjectVersionPopupPayload): void
  handleOpenDeleteProjectVersionPopup(payload: IOpenDeleteProjectVersionPopupPayload): void
  handleOpenSetupProjectOrRenewSubscriptionPopup(projectVersionId: string): void
}

const ProjectVersionRow: React.FC<ProjectVersionRowProps> = props => {
  const {
    index,
    projectVersion,
    isOriginal,
    sourceThumbnailUrl,
  } = props

  const {
    subtitles,
    id,
  } = projectVersion

  const {
    title,
    duration,
    status,
    projectId,
    thumbnailUrl,
  } = projectVersion.attributes

  const isPending = getProjectVersionDisplayKind(projectVersion.attributes) === 'pending_clip'

  const {
    showTooltip,
    setShowTooltip,
    triggerShowTooltip,
    cancelTooltip,
  } = useTooltipDelay()

  const [showDropdown, setShowDropdown] = useState(false)

  const transcript = getTranscript(projectVersion)

  const hasVoice = transcript?.attributes.hasVoice

  const moreElementId = `project_version_row_more_${index}`
  const classNameForClickOutsideListenerMoreDropdown = `ProjectVersionsTable__action-dropdown-element-${index}`
  const testIdSuffix = `${isOriginal ? 'source' : 'clip'}-${id}`
  const shouldAllowToDeleteProjectVersion = !isOriginal
  const numberOfLanguages = subtitles.length
  const numberOfDoneLanguages = getDoneLanguagesCount(projectVersion)

  const allowLinkToEditor = !!transcript && getAllowLinkToEditor(projectVersion.attributes.isDisabled, transcript.attributes)
  const shouldAllowToAddLanguages = getShouldAllowToAddLanguages(projectVersion.attributes)
  const hasDropdownOptions = !isPending && (shouldAllowToAddLanguages || shouldAllowToDeleteProjectVersion)

  // The shared `thumbnailUrl` can fall back to the project/original thumbnail, so a
  // clip preview is only trustworthy when it actually differs from the source video's.
  const hasTrustworthyClipThumbnail = !isOriginal && !!thumbnailUrl && thumbnailUrl !== sourceThumbnailUrl

  const openEditor = () => {
    if (!allowLinkToEditor || !transcript) { return }

    const urlParams = hasVoice ? '?openVoice=true' : ''
    window.location.href = `/projects/${projectId}/project_versions/${projectVersion.id}/subtitles/${transcript.id}/edit${urlParams}`
  }

  const openLanguages = () => {
    if (isPending) { return }

    props.resetOpenedProject()
    props.setOpenedProjectVersionId(projectVersion.id)
  }

  const toggleDropdown = () => {
    if (!hasDropdownOptions) { return }

    setShowDropdown(!showDropdown)
  }

  const onMouseEnterTitle = () => {
    if (isPending) { return }

    triggerShowTooltip()
  }

  const onMouseLeaveTitle = () => {
    cancelTooltip()
  }

  const handleOpenDeleteProjectVersionPopup = () => {
    if (!shouldAllowToDeleteProjectVersion) { return }

    props.handleOpenDeleteProjectVersionPopup({ projectVersionIds: [projectVersion.id] })
    setShowDropdown(false)
  }

  const handleOpenSetupProjectOrRenewSubscriptionPopup = () => {
    if (!shouldAllowToAddLanguages) { return }

    props.handleOpenSetupProjectOrRenewSubscriptionPopup(projectVersion.id)
    setShowDropdown(false)
  }

  const handleOpenRenameProjectVersionPopup = () => {
    props.handleOpenRenameProjectVersionPopup({ projectVersionId: projectVersion.id })
    setShowDropdown(false)
  }

  const renderPreview = () => {
    if (isPending) {
      return (
        <div
          className="ProjectVersionRow__preview ProjectVersionRow__preview-pending"
          aria-hidden="true"
        >
          <span className="ProjectVersionRow__preview-shimmer" />
          <span className="ProjectVersionRow__preview-glyph">
            <Magic />
          </span>
        </div>
      )
    }

    const showThumbnail = isOriginal ? !!thumbnailUrl : hasTrustworthyClipThumbnail

    if (showThumbnail) {
      return (
        <div className="ProjectVersionRow__preview ProjectVersionRow__preview-media">
          <img src={thumbnailUrl} alt="" loading="lazy" />
        </div>
      )
    }

    return (
      <div
        className={classnames(
          'ProjectVersionRow__preview',
          isOriginal ? 'ProjectVersionRow__preview-source' : 'ProjectVersionRow__preview-clip'
        )}
        aria-hidden="true"
      >
        <span className="ProjectVersionRow__preview-glyph">
          <Video />
        </span>
      </div>
    )
  }

  const renderTitle = () => {
    if (isPending) {
      return (
        <React.Fragment>
          <div className="ProjectVersionRow__title-text">
            ✨ AI is crafting a new clip...
          </div>
          <div className="ProjectVersionRow__tag ProjectVersionRow__clip">
            Clip
          </div>
        </React.Fragment>
      )
    }

    return (
      <React.Fragment>
        <div className="ProjectVersionRow__title-text">
          {getCleanDisplayTitle(title)}
        </div>
        {isOriginal ? (
          <div className="ProjectVersionRow__tag ProjectVersionRow__source">
            Source video
          </div>
        ) : (
          <div className="ProjectVersionRow__tag ProjectVersionRow__clip">
            Clip
          </div>
        )}
      </React.Fragment>
    )
  }

  const renderTooltip = () => {
    return (
      <TitleTooltip
        id={id}
        title={getCleanDisplayTitle(title)}
        elementId={`project_version_row_title_${id}`}
        tooltipOffsetX={-50}
        tooltipOffsetY={8}
        classNameOfScrollContainer="ProjectVersionsTable"
        setShowTooltip={setShowTooltip}
      />
    )
  }

  const renderStatus = () => {
    if (isPending) {
      return (
        <div className="ProjectVersionRow__status-badge in_progress">
          Processing
          <InProgress />
        </div>
      )
    }

    const [projectVersionStatusClass, projectVersionStatusToRender] = getProjectStatusForProjectsList([status])
    const isDone = projectVersionStatusClass === 'done'
    const isAwaitingOriginal = projectVersionStatusClass === 'awaiting_original'
    const isInProgress = projectVersionStatusClass === 'in_progress'

    return (
      <div className={`ProjectVersionRow__status-badge ${projectVersionStatusClass}`}>
        {projectVersionStatusToRender}
        {isDone && <Done />}
        {isInProgress && <InProgress />}
        {isAwaitingOriginal && <Pending />}
      </div>
    )
  }

  const actionDropdownList = (
    <ul>
      {shouldAllowToAddLanguages && (
        <li
          className="ProjectVersionsTable__action-dropdown-item"
          onClick={handleOpenSetupProjectOrRenewSubscriptionPopup}
          data-testid={`project-version-add-languages-${testIdSuffix}`}
        >
          <div className="ProjectVersionsTable__action-dropdown-item-text">
            Add languages
          </div>
        </li>
      )}
      <li
        className="ProjectVersionsTable__action-dropdown-item"
        onClick={handleOpenRenameProjectVersionPopup}
        data-testid={`project-version-rename-${testIdSuffix}`}
      >
        <div className="ProjectVersionsTable__action-dropdown-item-text">
          Rename
        </div>
      </li>
      {shouldAllowToDeleteProjectVersion && (
        <li
          className="ProjectVersionsTable__action-dropdown-item"
          onClick={handleOpenDeleteProjectVersionPopup}
          data-testid={`project-version-delete-${testIdSuffix}`}
        >
          <div className="ProjectVersionsTable__action-dropdown-item-text">
            Delete clip
          </div>
        </li>
      )}
    </ul>
  )

  const renderActionDropdown = () => {
    return (
      <ActionDropdown
        showDropdown={showDropdown}
        dropdownList={actionDropdownList}
        classNamesForListContainer="ProjectVersionsTable__action-dropdown ProjectVersionsView__element"
        classNameForClickOutsideListener={classNameForClickOutsideListenerMoreDropdown}
        classNameOfScrollContainer="ProjectVersionsView"
        idOfDropdownElement={moreElementId}
        idOfPortalContainer="dashboard"
        dropdownOffsetX={45}
        dropdownOffsetY={0}
        dropdownWidth={209} // same width as ProjectVersionsTable__action-dropdown
        setShowDropdown={setShowDropdown}
      />
    )
  }

  return (
    <tr
      className={classnames(
        { 'ProjectVersionRow__even': index % 2 === 0 },
        { 'ProjectVersionRow__uneven': index % 2 === 1 },
        { 'ProjectVersionRow__pending': isPending },
        { 'ProjectVersionRow__openable': allowLinkToEditor }
      )}
      aria-busy={isPending || undefined}
    >
      <td className="ProjectVersionsTable__preview ProjectVersionRow__preview-cell">
        {renderPreview()}
      </td>
      <td
        className={classnames(
          'ProjectVersionsTable__title',
          'ProjectVersionRow__title',
          { 'ProjectVersionRow__link': allowLinkToEditor },
        )}
        onClick={isPending ? undefined : openEditor}
        data-testid={!isPending ? `project-version-open-editor-title-${testIdSuffix}` : undefined}
        id={`project_version_row_title_${id}`}
        onMouseEnter={onMouseEnterTitle}
        onMouseLeave={onMouseLeaveTitle}
      >
        {renderTitle()}
        {showTooltip && renderTooltip()}
      </td>
      <td
        className={classnames(
          'ProjectVersionsTable__duration',
          'ProjectVersionRow__duration',
          { 'ProjectVersionRow__link': allowLinkToEditor },
        )}
        onClick={isPending ? undefined : openEditor}
        data-testid={!isPending ? `project-version-open-editor-duration-${testIdSuffix}` : undefined}
      >
        {isPending
          ? <span className="ProjectVersionRow__pending-text">Preparing</span>
          : getFormattedDuration(duration)}
      </td>
      <td
        className={classnames(
          'ProjectVersionsTable__status',
          'ProjectVersionRow__status',
          { 'ProjectVersionRow__link': allowLinkToEditor },
        )}
        onClick={isPending ? undefined : openEditor}
        data-testid={!isPending ? `project-version-open-editor-status-${testIdSuffix}` : undefined}
      >
        {renderStatus()}
      </td>
      <td
        className={classnames(
          'ProjectVersionsTable__languages',
          'ProjectVersionRow__languages',
          { 'ProjectVersionRow__link': !isPending },
        )}
        onClick={openLanguages}
        data-testid={!isPending ? `project-version-open-languages-${testIdSuffix}` : undefined}
      >
        {isPending
          ? <span className="ProjectVersionRow__pending-text">-</span>
          : `${numberOfDoneLanguages}/${numberOfLanguages}`}
      </td>
      <td
        className={classnames(
          'ProjectVersionsTable__more',
          'ProjectVersionRow__more',
          { 'ProjectVersionRow__more-disabled': !hasDropdownOptions}
        )}
      >
        {!isPending && (
          <div
            className={`ProjectVersionRow__more-wrapper ${classNameForClickOutsideListenerMoreDropdown}`}
            id={moreElementId}
          >
            <More
              onClick={toggleDropdown}
              data-testid={`project-version-more-actions-${testIdSuffix}`}
            />
            {showDropdown && renderActionDropdown()}
          </div>
        )}
      </td>
    </tr>
  )
}

export default ProjectVersionRow
